import React, { useState, useEffect } from 'react';
import { useRouteData, useSiteData, Head } from 'react-static';
import LazyLoad from 'react-lazyload';
// template components
import ContentHub from '@nmx/template/dist/componentLibrary/modules/ContentHub';
import InPageLeadForm from '@nmx/template/dist/componentLibrary/modules/InPageLeadForm';
import Page from '@nmx/template/dist/componentLibrary/layout/Page';
import Theme from '@nmx/template/dist/componentLibrary/Theme';
// native components
import AdvisorConnectModule from './components/advisorConnectModule';
import CredentialCalloutGrouping from './components/credentialCalloutGrouping';
import ExperienceCallout from './components/experienceCallout';
import Faq from './components/faq';
import FinancialPlanningSection1 from './components/financialPlanningSection1';
import FinancialPlanningSection2 from './components/financialPlanningSection2';
import HeroSection from './components/heroSection';
import InPageLeadFormLazyLoadContainer from '../../components/inPageLeadFormLazyLoadContainer';
import IntroSection from './components/introSection';
import Metadata from './components/metadata';
import ProductCalloutGrouping from './components/productCalloutGrouping';
import SuspectEmailCaptureFormSection from './components/suspectEmailCaptureFormSection';
// images for prefetch
import highResDesktopImage from './components/heroSection/images/insurance-hero.webp';
import highResMobileImage from './components/heroSection/images/insurance-hero-mobile.webp';
// dislaimer footnotes
import {
  DividendsAreNotGuaranteed,
  GlobalMarketIntelligenceForDisability,
  HighestFinancialStrengthRatings,
  NerdWalletAllRightsReserved,
  YourPolicysCashValue,
} from '../../../utils/disclaimerFootnotes/disclaimerFootnotes';
// other utils
import { getImmediateMatchFeatureFlag } from '../../../utils/getFeatureFlags/getFeatureFlags';

export default () => {
  const { config, dynamicConfig } = useSiteData();
  const { pageProps, contentHubItems } = useRouteData();
  // We don't want to load recaptcha on page load (for performance reasons) so we will load it once the in page lead for is engaged with
  const [loadRecaptcha, setLoadRecaptcha] = useState(false);
  const [immediateMatchFeatureFlag, setImmediateMatchFeatureFlag] = useState(false);

  useEffect(() => {
    getImmediateMatchFeatureFlag(setImmediateMatchFeatureFlag, config);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page
      {...pageProps} // defined in route.js
      headComponent={Head}
      config={config}
      dynamicConfig={dynamicConfig}
      useReCaptcha={loadRecaptcha}
      disableDisclaimerTopBorder
      disclaimerFootnotes={[
        <>
          {HighestFinancialStrengthRatings(1)}
        </>,
        <>
          <sup>2</sup>&nbsp;Unless you cancel or stop paying your premiums or outlive you term coverage.
        </>,
        <>
          <sup>3</sup>&nbsp;All guarantees backed solely by the claims&#45;paying ability of the insurance company.
        </>,
        <>
          {YourPolicysCashValue(4)}
        </>,
        <>
          {DividendsAreNotGuaranteed(5)}
        </>,
        <>
          {NerdWalletAllRightsReserved(6)}
        </>,
        <>
          {GlobalMarketIntelligenceForDisability(7)}
        </>,
        <>
          To be used with forms ICC16.TT.GRDI.&#40;0916&#41;, ICC16.TT.DOE.&#40;0916&#41;, TT.KEY.&#40;0617&#41;, TT.BUY.&#40;0617&#41;, MN 992&#45;STD, MN 992&#45;LTD, ICC16.TT.NCDI.&#40;0916&#41;, ICC12.TT.TERM.&#40;0513&#41;, ICC12.TT.TERM.L20.&#40;0513&#41;, ICC16 TT 916&#45;LIFE, ICC21.UU.WL.&#40;1121&#41;, ICC17.UU.WL.&#40;0119&#41;, ICC17.UU.SWL.&#40;0119&#41;, ICC17.UU.PSPIA.&#40;0717&#41;, ICC17.UU.PDIA.&#40;0717&#41;, ICC11.NN.SPIA.&#40;0511&#41;, ICC16.NN.DIA.&#40;1116&#41;, ICC12.RS.SPRA.&#40;0313&#41;, ICC12.RS.SPRA.5.&#40;0313&#41;, ICC12.RS.SPRA.LD.&#40;0313&#41; or state equivalent. Not all contracts and optional benefits are available in all states.
        </>,
      ]}
      breadcrumbsNavigationConfig={[
        {
          pageName: 'Home',
          linkUrl: '/',
          linkAriaLabel: 'Northwestern Mutual Home',
        },
        {
          pageName: 'Insurance',
          linkUrl: '/insurance/',
          linkAriaLabel: 'Northwestern Mutual Insurance',
        },
      ]}
      stateFilingComplianceNumber={<>90&#8211;26683 &#40;1122&#41;</>}
    >
      <Head>
        {/* prefetch expensive images */}
        <link rel='preload' href={highResMobileImage} as='image' media={`(max-width: ${Theme.breakpoints.values.medium}px)`} />
        <link rel='preload' href={highResDesktopImage} as='image' media={`(min-width: ${Theme.breakpoints.values.medium + 1}px)`} />
      </Head>

      <Metadata
        image={highResDesktopImage}
        pageDescription={pageProps.pageDescription}
        pageTitle={pageProps.pageTitle}
      />

      <HeroSection />

      <IntroSection />

      <ProductCalloutGrouping />

      {/* Suspect email capture form */}
      <LazyLoad
        offset={300}
        height={300}
        once
        placeholder={
          <section id='life-insurance-page-suspect-email-capture-form'>
            <h2>Get weekly emails to help you reach your goals, big or small.</h2>
          </section>
        }>
        <SuspectEmailCaptureFormSection
          sectionId='life-insurance-page-suspect-email-capture-form'
          config={config}
          loadRecaptcha={loadRecaptcha}
          setLoadRecaptcha={setLoadRecaptcha}
        />
      </LazyLoad>

      <Faq />

      {/* IN-PAGE LEAD FORM SECTION */}
      <InPageLeadFormLazyLoadContainer>
        <InPageLeadForm
          sectionId='in-page-lead' // note: id in-page-lead-form will be on the Form element itself.
          config={config}
          formLocation='insurance'
          immediateMatchFeatureFlagEnabled={immediateMatchFeatureFlag}
          onFormEngaged={() => {
            if (!loadRecaptcha) {
              setLoadRecaptcha(true);
            }
          }}
          variant='embedded'
        />
      </InPageLeadFormLazyLoadContainer>

      <FinancialPlanningSection1 />

      {/* EXPERIENCE CALLOUT */}
      <LazyLoad
        offset={200}
        height={300}
        once
        placeholder={
          <section id='insurance-calc-experience-callout'>
            <h2>Life Insurance Calculator</h2>
            <p>How much life insurance is right for you&#63;</p>
          </section>
        }>
        <ExperienceCallout />
      </LazyLoad>

      <FinancialPlanningSection2 />

      <CredentialCalloutGrouping />

      <ContentHub
        sectionId='content-hub'
        headingText='Boost your insurance knowledge'
        contentHubItems={contentHubItems} // defined in route.js
        config={config}
      />

      <AdvisorConnectModule />

    </Page>
  );
};
